import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, Navigate } from 'react-router-dom';
import { hasPermission as checkPermission } from '../utils/permissionHelper';

// The gate pages; a guard is kept on them.
// …and their training (Learn the flow, 18 Oct 2026).
const GUARD_PATHS = ['/move/gate', '/procure/arrivals', '/no-access', '/learn'];

/**
 * Section-based route guard.
 * Prefer section + action props. Legacy allowedRoles / allowedPermissions still supported.
 */
export default function ProtectedRoute({
  children,
  section,
  sections,
  action = 'view',
  allowedRoles,
  allowedPermissions,
  fallback,
  denyRoles,
}) {
  const { isAuthenticated, user, loading, effectivePermissions } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    // A warehouse QR opened while signed out (phone camera): come back to it after login.
    if (/^\/scan\/[a-f0-9]+$/i.test(location.pathname)) {
      try { sessionStorage.setItem('carret.afterLogin', location.pathname); } catch { /* storage off: lands on home */ }
    }
    return <Navigate to="/login" />;
  }

  let allowed = true;

  if (Array.isArray(sections) && sections.length) {
    // Allow if the user can view ANY of the given sections (module umbrella guard).
    allowed = sections.some((s) => checkPermission(user, effectivePermissions, s, action));
  } else if (section) {
    allowed = checkPermission(user, effectivePermissions, section, action);
  } else if (allowedRoles || allowedPermissions) {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const hasRole = !allowedRoles || (user && allowedRoles.includes(user.role));
    const hasLegacyPermission =
      !allowedPermissions || allowedPermissions.some((p) => perms.includes(p));
    allowed = hasRole || hasLegacyPermission;
  }

  // Desk pages a role must not open even though it holds the section — a field
  // technician has support_tickets view for their own jobs (19 Oct 2026).
  // They go to their own home page (old /support links land on the queue).
  if (allowed && Array.isArray(denyRoles) && denyRoles.includes(user?.role)) return <Navigate to="/" replace />;

  if (!allowed) {
    if (user?.role === 'guard') {
      return <Navigate to="/move/gate" replace />;
    }
    // A refused new-UI page says so in the new UI; anything else goes home
    // ('/' picks the first page the user may open). Never the old /dashboard,
    // which is not part of the new UI (15 Oct 2026).
    if (fallback) return <Navigate to={fallback} replace />;
    return <Navigate to="/no-access" replace state={{ from: location.pathname }} />;
  }

  // A guard works the gate only (18 Oct 2026: the gate pages are in the new UI).
  if (user?.role === 'guard' && !GUARD_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/move/gate" replace />;
  }

  return children;
}
